import { useBreakpoints } from '@ux/component-utilities';
import React, { Component } from 'react';
import { URL } from '@ux/util';
import PropTypes from 'prop-types';
import Link from '@ux/link';
import goLogo from './goLogo.js';
import classnames from 'classnames';

// Regex for whitespace and non-typographical unicode characters
// eslint-disable-next-line no-control-regex
const WHITESPACE_REGEX = /[\s\u0000-\u001F\u007F-\u009F]/g;

/**
 * Add query parameters from props to the url.
 *
 * @param {String} url link.
 * @param {Object} props Properties to merge with the query parameters object.
 * @returns {String} Configured url.
 * @private
 */
function updateQuery(url, props) {
  const urlObj = new URL(url, true);
  return urlObj.set('query', {
    ...urlObj.query,
    ...props
  }).href;
}

/**
 * Renders shared GD logo.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} shared logo
 * @public
 */
export class LogoComponent extends Component {
  constructor(props) {
    super(props);

    const { isMobile, collapsedLogo } = props;
    // Some Logo consumers setup breakpoints themselves and will trigger
    // corresponding re-renders of the Logo. But for those that don't, Logo
    // needs to setup breakpoints on its own. This prevents spurious re-renders
    // when consumed by an already-breakpoint-sensitive component.
    this.shouldConfigureBreakpoints = typeof isMobile !== 'boolean' && !collapsedLogo;
  }

  /**
   * Check if breakpoint is mobile. (Respects `shouldConfigureBreakpoints`.)
   *
   * @returns {Boolean} Mobile or not.
   * @private
   */
  get _isMobile() {
    return this.shouldConfigureBreakpoints
      ? this.isMobile
      : this.props.isMobile;
  }

  /**
   * Get the logo URL for the current breakpoint.
   *
   * @returns {String} Logo URL.
   * @private
   */
  get _logo() {
    const { logos = {}, collapsedLogo } = this.props;
    const url = (this._isMobile || collapsedLogo) ? logos.mobile : logos.desktop;

    return (url || '').replace(WHITESPACE_REGEX, '');
  }

  /**
   * Return elements for rendering a reseller logo
   * @returns {React.ReactElement} Part of the shared logo
   * @api private
   */
  resellerLogo() {
    const { spanClass, privateLabelName } = this.props;
    const imgSrc = this._logo;

    //
    // If no URL for a custom logo has been provided then display
    // the name of the private label.
    //
    if (!imgSrc) {
      return (
        <span id='registrar' className='logo-text'>
          { privateLabelName }
        </span>
      );
    }

    // To prevent SVGs from rendering 0x0, set an arbitrarily large enough width
    // to cover the max-width of the span. Only appropriate for SVGs because
    // other image types would stretch if their aspect ratio is narrower than
    // the span's.
    const svgHack = /\.svg$/.test(imgSrc) && {
      width: 200
    };

    return (
      <span className={ classnames('logo-img', spanClass) }>
        <img
          id='registrar'
          src={ encodeURI(imgSrc) }
          alt={ privateLabelName }
          { ...svgHack }
        />
      </span>
    );
  }

  /**
   * Work around for `sinon@7`/lolex having issues faking timers.
   * Context
   *
   * @returns {number} number of milliseconds since epoch (same as `Date.now()`)
   */
  getDateNow() {
    return Date.now();
  }

  /**
   * Return elements for rendering the temporary GoDaddy logo for culture campaigns
   *
   * @param {Object} tempLogo from props
   * @param {String} tempLogo.filename the name of the file to be retrieved from CDN
   * @param {Object} tempLogo.start the starting date of the campaign
   * @param {Object} tempLogo.end the ending date of the campaign
   * @param {Object} [tempLogo.styles] the styles to be applied
   * @param {String} [tempLogo.altText] alternate text to set on logo image
   * @returns {React.ReactElement} The temp logo
   */
  renderTempLogo({ filename, start, end, styles = {}, altText = '' }) {
    const props = this.props;

    const convertToTimestamp = ({ year, month, day, hour = null }) => {
      // subtract 1 from the month since Date months are indexed from 0
      return new Date(year, month - 1, day, hour).getTime();
    };

    const startDate = convertToTimestamp(start);
    const endDate = convertToTimestamp(end);

    const now = this.getDateNow();

    if (startDate < now && now < endDate) {
      const cdn = `${ props.urls.cdn.href }ux/sales-header/`;

      return (
        <span className={ props.spanClass } aria-label='GoDaddy'>
          <img
            alt={ altText }
            src={ `${ cdn }${ filename }` }
            onError={ e => {
              // @ts-ignore
              /** @type {HTMLImageElement} */ const t = e.target;
              t.onerror = null;
              // default logo fallback
              // TODO: update default logo fallback
              t.src = `${ cdn }gd-logo.svg`;
              t.setAttribute('style', null);
            } }
            style={ styles }
          />
        </span>
      );
    }

    return this.renderGoLogo();
  }

  renderGoLogo() {
    const props = this.props;
    // NOTE: `sr-only` is a bootstrap classname and cannot be namespaced
    const srClasses = classnames('sr-only', 'sr-logo');
    const useMobile = this._isMobile || props.collapsedLogo;
    const spanClass = useMobile ? 'mobile-logo' : 'desktop-logo';

    return (
      <span
        className={ classnames('go-logo', spanClass, props.spanClass) }
        aria-label='GoDaddy'>
        <span className={ srClasses }>GoDaddy</span>
        { useMobile ? goLogo.mobile : goLogo.desktop }
      </span>
    );
  }

  render() {
    const props = this.props;
    const linkProps = {
      'className': classnames(props.className, {
        'white-logo-bg': props.privateLabelId !== 1 && this._logo
      }),

      'urlArgs': props.urlArgs,
      'data-eid': props['data-eid'],
      'onClick': props.onClick
    };

    let logoContent;

    if (props.privateLabelId !== 1) {
      linkProps.rel = 'nofollow';
      logoContent = this.resellerLogo();
    } else if (props.tempLogo) {
      logoContent = this.renderTempLogo(props.tempLogo);
    } else {
      logoContent = this.renderGoLogo();
    }

    const logo = (
      <React.Fragment>
        {props.market !== 'en-US' &&
          props.showCountryNameOnLogo &&
          props.privateLabelId === 1 && (
          <span className='topnav-country'>{ props.country }</span>
        )}
        { logoContent }
      </React.Fragment>
    );

    if (!props.href) return logo;

    // UXP-4071 -- Ensure that we don't attempt to construct a URL when there is no href set
    linkProps.href = updateQuery(props.href, props.urlArgs);

    return <Link { ...linkProps }>{ logo }</Link>;
  }
}

LogoComponent.defaultProps = {
  country: '',
  market: 'en-US',
  privateLabelId: 1,
  showCountryNameOnLogo: false,
  urlArgs: {}
};

LogoComponent.propTypes = {
  'className': PropTypes.string,
  'country': PropTypes.string,
  'data-eid': PropTypes.string,
  'href': PropTypes.string,
  'logos': PropTypes.object,
  'market': PropTypes.string.isRequired,
  'messages': PropTypes.object.isRequired,
  'privateLabelId': PropTypes.number.isRequired,
  'privateLabelName': PropTypes.string,
  'showCountryNameOnLogo': PropTypes.bool,
  'spanClass': PropTypes.string,
  'tempLogo': PropTypes.object,
  'urlArgs': PropTypes.object,
  'urls': PropTypes.object,
  'isMobile': PropTypes.bool,
  'collapsedLogo': PropTypes.bool,
  'id': PropTypes.string,
  'onClick': PropTypes.func
};

export default function LogoComponentWrap(props) {
  const { breakpoint } = useBreakpoints('mobile', 'phablet', 'tablet', 'desktop', 'large');
  const { isMobile, collapsedLogo } = props;
  if (typeof isMobile !== 'boolean' && !collapsedLogo) {
    return <LogoComponent { ...props } isMobile={ ['mobile', 'phablet'].includes(breakpoint) }/>;
  }
  return <LogoComponent { ...props }/>;
}

LogoComponentWrap.propTypes = {
  isMobile: PropTypes.bool,
  collapsedLogo: PropTypes.bool
};
